/**
 * Turret Definitions (Simplified)
 *
 * 4 core turret types for the defense system.
 * Each turret starts with Natural class and can be upgraded to fortress class.
 *
 * Layout slotów (6 stałych):
 * [SLOT 1] [SLOT 2] [SLOT 3]
 *     |________|________|
 *          ZAMEK
 *     |________|________|
 * [SLOT 4] [SLOT 5] [SLOT 6]
 */
export const TURRET_ID_MAP = {
    'arrow': 'railgun',
    'frost': 'cryo',
    'cannon': 'artillery',
    'tesla': 'arc',
};
export const REVERSE_TURRET_ID_MAP = {
    'railgun': 'arrow',
    'cryo': 'frost',
    'artillery': 'cannon',
    'arc': 'tesla',
};
// ============================================================================
// MODYFIKATORY KLAS DLA WIEŻYCZEK
// ============================================================================
export const TURRET_CLASS_MODIFIERS = [
    {
        class: 'natural',
        damageMultiplier: 16384, // 1.0
        attackSpeedMultiplier: 16384,
        rangeMultiplier: 16384,
    },
    {
        class: 'ice',
        damageMultiplier: 14746, // 0.9
        attackSpeedMultiplier: 16384,
        rangeMultiplier: 16384,
        specialBonus: '+30% slow duration',
    },
    {
        class: 'fire',
        damageMultiplier: 19661, // 1.2
        attackSpeedMultiplier: 16384,
        rangeMultiplier: 16384,
        specialBonus: '+20% DOT damage',
    },
    {
        class: 'lightning',
        damageMultiplier: 16384, // 1.0
        attackSpeedMultiplier: 21299, // 1.3
        rangeMultiplier: 16384,
        specialBonus: '+1 chain target',
    },
    {
        class: 'tech',
        damageMultiplier: 16384, // 1.0
        attackSpeedMultiplier: 16384,
        rangeMultiplier: 19661, // 1.2
        specialBonus: '+1 pierce',
    },
];
// ============================================================================
// DEFINICJE WIEŻYCZEK
// ============================================================================
const RAILGUN_TOWER = {
    id: 'railgun',
    name: 'Wieża Railgun',
    description: 'Szybka wieża strzelająca pociskami elektromagnetycznymi. Idealna do eliminacji pojedynczych celów.',
    role: 'dps',
    baseStats: {
        damage: 131072, // 8.0
        attackSpeed: 40960, // 2.5 ataki/s
        range: 163840, // 10 jednostek
        critChance: 1638, // 10%
        critMultiplier: 24576, // 1.5x
        hp: 150, // Fragile, high DPS
    },
    baseCost: { gold: 3000 },
    tierCostMultiplier: 32768, // 2.0
    classChangeCost: { gold: 150, dust: 15 },
    ability: {
        id: 'rapid_fire',
        name: 'Rapid Fire',
        description: 'Podwaja szybkość ataku na 5 sekund.',
        cooldown: 900, // 30 sekund
        effect: {
            type: 'damage_boost',
            value: 32768, // 2.0x attack speed
            duration: 150, // 5 sekund
        },
    },
    projectileType: 'arrow',
    colors: {
        primary: 0x4A5568, // Slate Gray (Sci-Fi)
        secondary: 0x718096, // Gray-blue
        projectile: 0x00BFFF, // Deep Sky Blue (energy projectile)
    },
};
const ARTILLERY_TOWER = {
    id: 'artillery',
    name: 'Wieża Artyleryjska',
    description: 'Powolna ale potężna wieża z obrażeniami obszarowymi.',
    role: 'aoe',
    baseStats: {
        damage: 737280, // 45.0
        attackSpeed: 8192, // 0.5 ataki/s
        range: 131072, // 8 jednostek
        critChance: 819, // 5%
        critMultiplier: 32768, // 2.0x
        hp: 200, // Medium durability
    },
    baseCost: { gold: 8000 }, // Increased from 5500 for economy balance
    tierCostMultiplier: 32768,
    classChangeCost: { gold: 150, dust: 15 },
    ability: {
        id: 'explosive_shell',
        name: 'Explosive Shell',
        description: 'Następny strzał zadaje 200% obrażeń z większym splashem.',
        cooldown: 600, // 20 sekund
        effect: {
            type: 'aoe_attack',
            value: 32768, // 2.0x damage
            radius: 3,
        },
    },
    projectileType: 'cannonball',
    colors: {
        primary: 0x2F4F4F, // DarkSlateGray
        secondary: 0x696969, // DimGray
        projectile: 0x1C1C1C, // Ciemna kula
    },
    specialEffects: {
        splash: true,
        splashRadius: 24576, // 1.5 jednostki
    },
};
const ARC_TOWER = {
    id: 'arc',
    name: 'Wieża Łukowa',
    description: 'Elektryczna wieża, której łuki energetyczne przeskakują między wrogami.',
    role: 'aoe',
    baseStats: {
        damage: 245760, // 15.0
        attackSpeed: 19661, // 1.2 ataki/s
        range: 114688, // 7 jednostek
        critChance: 1638, // 10%
        critMultiplier: 24576, // 1.5x
        hp: 180, // Medium
    },
    baseCost: { gold: 7000 },
    tierCostMultiplier: 32768,
    classChangeCost: { gold: 150, dust: 15 },
    ability: {
        id: 'overload',
        name: 'Overload',
        description: 'Następny atak uderza we WSZYSTKICH wrogów w zasięgu.',
        cooldown: 750, // 25 sekund
        effect: {
            type: 'chain_all',
        },
    },
    projectileType: 'lightning',
    colors: {
        primary: 0x4B0082, // Indigo
        secondary: 0x9932CC, // DarkOrchid
        projectile: 0x00FFFF, // Cyan (energy arc)
    },
    specialEffects: {
        chainTargets: 3,
        chainDamageReduction: 4915, // -30% na każdy skok
    },
};
const CRYO_TOWER = {
    id: 'cryo',
    name: 'Wieża Kriogeniczna',
    description: 'Kriogeniczna wieża spowalniająca wrogów i zamrażająca ich na miejscu.',
    role: 'crowd_control',
    baseStats: {
        damage: 196608, // 12.0
        attackSpeed: 16384, // 1.0 ataki/s
        range: 131072, // 8 jednostek
        critChance: 1638, // 10%
        critMultiplier: 24576, // 1.5x
        hp: 200, // Sturdy CC
    },
    baseCost: { gold: 3500 },
    tierCostMultiplier: 32768,
    classChangeCost: { gold: 150, dust: 15 },
    ability: {
        id: 'flash_freeze',
        name: 'Flash Freeze',
        description: 'Zamraża wszystkich wrogów w zasięgu na 3 sekundy.',
        cooldown: 900, // 30 sekund
        effect: {
            type: 'freeze_all',
            duration: 90, // 3 sekundy
        },
    },
    projectileType: 'ice_shard',
    colors: {
        primary: 0x00CED1, // DarkTurquoise
        secondary: 0x87CEEB, // SkyBlue
        projectile: 0xADD8E6, // LightBlue
    },
    specialEffects: {
        slowAmount: 8192, // 50% slow
        slowDuration: 60, // 2 sekundy
    },
};
// ============================================================================
// EKSPORT
// ============================================================================
export const TURRET_DEFINITIONS = [
    RAILGUN_TOWER,
    CRYO_TOWER,
    ARTILLERY_TOWER,
    ARC_TOWER,
];
// Legacy exports for backwards compatibility
export const ARROW_TOWER = RAILGUN_TOWER;
export const FROST_TOWER = CRYO_TOWER;
export const CANNON_TOWER = ARTILLERY_TOWER;
export const TESLA_TOWER = ARC_TOWER;
export const TURRET_SLOTS = [
    // Górne sloty (nad ścieżką, PRZED zamkiem - w kierunku wrogów)
    { id: 1, position: 'top', index: 0, offsetX: 4, offsetY: -3 },
    { id: 2, position: 'top', index: 1, offsetX: 7, offsetY: -3.5 },
    { id: 3, position: 'top', index: 2, offsetX: 10, offsetY: -3 },
    // Dolne sloty (pod ścieżką, PRZED zamkiem - w kierunku wrogów)
    { id: 4, position: 'bottom', index: 0, offsetX: 4, offsetY: 3 },
    { id: 5, position: 'bottom', index: 1, offsetX: 7, offsetY: 3.5 },
    { id: 6, position: 'bottom', index: 2, offsetX: 10, offsetY: 3 },
];
// Dodatkowe sloty (odblokowywane za dust)
export const EXTRA_TURRET_SLOTS = [
    { id: 7, position: 'top', index: 3, offsetX: 13, offsetY: -2.5 },
    { id: 8, position: 'bottom', index: 3, offsetX: 13, offsetY: 2.5 },
];
// ============================================================================
// FUNKCJE POMOCNICZE
// ============================================================================
/**
 * Normalizes turret ID from legacy to new format
 */
export function normalizeTurretId(id) {
    if (id in TURRET_ID_MAP) {
        return TURRET_ID_MAP[id];
    }
    if (['railgun', 'cryo', 'artillery', 'arc'].includes(id)) {
        return id;
    }
    return undefined;
}
/**
 * Pobiera definicję wieżyczki po ID
 * Przyjmuje string dla kompatybilności z typami runtime - zwraca undefined dla nieznanych ID
 * Supports both legacy IDs (arrow, frost, cannon, tesla) and new IDs (railgun, cryo, artillery, arc)
 */
export function getTurretById(id) {
    const normalizedId = normalizeTurretId(id);
    if (!normalizedId)
        return undefined;
    return TURRET_DEFINITIONS.find(t => t.id === normalizedId);
}
/**
 * Pobiera wieżyczki według roli
 */
export function getTurretsByRole(role) {
    return TURRET_DEFINITIONS.filter(t => t.role === role);
}
/**
 * Oblicza koszt wieżyczki na danym tierze
 */
export function calculateTurretCost(turret, tier) {
    // Tier 1 = base cost; each tier multiplies by tierCostMultiplier (16384 = 1.0)
    const effectiveTier = Math.max(1, tier);
    const multiplier = turret.tierCostMultiplier / 16384;
    return Math.floor(turret.baseCost.gold * Math.pow(multiplier, effectiveTier - 1));
}
/**
 * Pobiera modyfikator klasy dla wieżyczki
 */
export function getTurretClassModifier(fortressClass) {
    return TURRET_CLASS_MODIFIERS.find(m => m.class === fortressClass);
}
/**
 * Oblicza finalne statystyki wieżyczki z uwzględnieniem klasy i tieru
 */
export function calculateTurretStats(turret, fortressClass, tier) {
    const classModifier = getTurretClassModifier(fortressClass);
    const tierMultiplier = 16384 + (tier - 1) * 4096; // 1.0, 1.25, 1.5 per tier
    if (!classModifier) {
        return { ...turret.baseStats };
    }
    return {
        damage: Math.floor((turret.baseStats.damage * classModifier.damageMultiplier * tierMultiplier) / (16384 * 16384)),
        attackSpeed: Math.floor((turret.baseStats.attackSpeed * classModifier.attackSpeedMultiplier * tierMultiplier) / (16384 * 16384)),
        range: Math.floor((turret.baseStats.range * classModifier.rangeMultiplier * tierMultiplier) / (16384 * 16384)),
        critChance: turret.baseStats.critChance,
        critMultiplier: turret.baseStats.critMultiplier,
    };
}
/**
 * Sprawdza czy slot jest odblokowany
 */
export function isSlotUnlocked(slotId, unlockedExtraSlots) {
    // Sloty 1-6 są zawsze odblokowane
    if (slotId <= 6)
        return true;
    // Sloty 7-8 wymagają odblokowania
    return unlockedExtraSlots.includes(slotId);
}
/**
 * Pobiera dostępne sloty
 */
export function getAvailableSlots(unlockedExtraSlots = []) {
    const baseSlots = [...TURRET_SLOTS];
    const extraSlots = EXTRA_TURRET_SLOTS.filter(slot => unlockedExtraSlots.includes(slot.id));
    return [...baseSlots, ...extraSlots];
}
/**
 * Koszt odblokowania dodatkowego slotu
 */
export const EXTRA_SLOT_UNLOCK_COST = {
    slot7: { dust: 700 },
    slot8: { dust: 700 },
};
/**
 * Oblicza HP wieżyczki na podstawie tieru
 * HP skaluje się: tier 1 = 1.0x, tier 2 = 1.25x, tier 3 = 1.5x
 */
export function calculateTurretHp(turret, tier) {
    const tierMultiplier = 1 + (tier - 1) * 0.25;
    return Math.floor(turret.baseStats.hp * tierMultiplier);
}
//# sourceMappingURL=turrets.js.map